import ScrollReveal from './ScrollReveal';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const Services = () => {
  const { t } = useTranslation();

  const services = t('services.items', {
    returnObjects: true,
  });

  return (
    <section className='relative px-6 py-10 overflow-hidden bg-white md:py-10 md:px-8'>
      {/* BACKGROUND */}

      <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
        <div
          className='
            w-[360px]
            h-[360px]
            sm:w-[460px]
            sm:h-[460px]
            md:w-[520px]
            md:h-[520px]
            rounded-full
            bg-gradient-to-br
            from-[#E6F4EF]
            via-transparent
            to-[#F3FBF8]
            blur-3xl
            opacity-60
          '
        />
      </div>

      <div className='relative mx-auto max-w-7xl'>
        {/* HEADER */}

        <ScrollReveal>
          <div className='max-w-2xl mb-16 md:mb-24'>
            <p
              className='
                text-[#2F8F6F]
                uppercase
                tracking-[0.25em]
                text-[11px]
                mb-5
                font-medium
              '
            >
              {t('services.label')}
            </p>

            <h2
              className='
                font-semibold
                mb-6
                text-[32px]
                sm:text-[40px]
                md:text-[52px]
                leading-tight
                tracking-tight
              '
            >
              {t('services.title')}
            </h2>

            <p className='text-sm leading-relaxed text-neutral-600 md:text-base'>
              {t('services.desc')}
            </p>
          </div>
        </ScrollReveal>

        {/* GRID */}

        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4'>
          {services.map((service, i) => (
            <ScrollReveal key={service.slug} delay={i * 0.06}>
              <NavLink
                to={`/portfolio/${service.slug}`}
                className='relative transition-all duration-300 ease-out bg-white border group border-neutral-200 rounded-2xl hover:shadow-xl hover:border-neutral-300 hover:-translate-y-1'
              >
                {/* hover background */}

                <div
                  className='
                    absolute
                    inset-0
                    rounded-2xl
                    opacity-0
                    transition
                    duration-300
                    bg-gradient-to-b
                    from-[#F3FBF8]
                    to-white
                    group-hover:opacity-100
                  '
                />

                {/* CONTENT */}

                <div className='relative z-10 flex flex-row items-start gap-4 p-5 md:flex-col md:h-full md:p-8'>
                  {/* ICON */}

                  <div
                    className='
                      flex
                      items-center
                      justify-center

                      w-12
                      h-12
                      shrink-0

                      rounded-xl
                      bg-[#E6F4EF]
                      text-[#2F8F6F]

                      transition
                      duration-300

                      group-hover:scale-105
                      group-hover:bg-[#D8EEE7]
                    '
                  >
                    {service.icon}
                  </div>

                  {/* TEXT AREA */}

                  <div className='flex flex-col flex-1'>
                    {/* NUMBER */}

                    <div className='mb-1 text-[10px] font-medium tracking-widest text-neutral-300 md:mb-6'>
                      0{i + 1}
                    </div>

                    {/* TITLE */}

                    <h3 className='mb-2 text-base font-semibold tracking-tight md:text-xl'>
                      {service.title}
                    </h3>

                    {/* TAGS — 모바일에서는 숨김 */}

                    {service.items && (
                      <div className='flex-wrap hidden gap-2 mb-6 md:flex'>
                        {service.items.map((item, idx) => (
                          <span
                            key={idx}
                            className='
                              px-2.5
                              py-1
                              text-xs
                              rounded-full
                              bg-neutral-100
                              text-neutral-700
                              border
                              border-neutral-200
                            '
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* DESCRIPTION */}

                    <p className='text-sm leading-relaxed text-neutral-600'>
                      {service.desc}
                    </p>
                  </div>

                  {/* ARROW */}

                  <div
                    className='
                      absolute
                      right-4
                      top-1/2
                      -translate-y-1/2

                      md:bottom-6
                      md:right-6
                      md:top-auto
                      md:translate-y-0

                      transition
                      duration-300

                      text-neutral-300
                      group-hover:text-[#2F8F6F]
                      group-hover:translate-x-1
                    '
                  >
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </NavLink>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
